import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { enUS as en, is } from 'date-fns/locale';

import { day } from './milliseconds';

export { differenceInCalendarDays, isSameDay } from 'date-fns';

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const UTC_MIDNIGHT_PATTERN = /^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.\d+)?Z$/;

/**
 * Parse a date-like value into a Date. Treats date-only strings and
 * UTC-midnight ISO datetimes (`YYYY-MM-DDT00:00:00Z`) as local calendar
 * dates — `new Date(str)` would otherwise shift them to the previous day
 * west of UTC. Only those two shapes are clearly "calendar-date intent";
 * any other datetime keeps the normal moment-in-time parse.
 */
export function parseDate(value: string | Date): Date {
  if (value instanceof Date) {
    return value;
  }
  if (typeof value !== 'string') {
    return new Date(NaN);
  }
  if (DATE_ONLY_PATTERN.test(value)) {
    return parseISO(value);
  }
  const utcMidnightMatch = value.match(UTC_MIDNIGHT_PATTERN);
  if (utcMidnightMatch) {
    return parseISO(utcMidnightMatch[1]);
  }
  return new Date(value);
}

// The single date-fns locale map for the languages the product renders dates
// in. Import this instead of building a local `{ en, is }` map.
export const dateFnsLocales = {
  is,
  en,
};

export type DateDisplay =
  | 'date'
  | 'date-short'
  | 'date-short-fancy-no-year'
  | 'date-fancy'
  | 'date-fancy-short'
  | 'date-fancy-no-year'
  | 'date-month-short-year'
  | 'datetime'
  | 'datetime-short'
  | 'datetime-seconds'
  | 'weekday-datetime'
  | 'date-countdown';

function getDateDisplayFormat(display: DateDisplay) {
  switch (display) {
    case 'date':
      return 'dd / MMM / yyyy';
    case 'date-short':
      return 'dd / MMM';
    case 'date-short-fancy-no-year':
      return 'dd MMMM';
    case 'date-fancy':
      return 'MMMM dd, yyyy';
    case 'date-fancy-short':
      return 'MMM dd, yyyy';
    case 'date-fancy-no-year':
      return 'MMM dd';
    case 'date-month-short-year':
      return "MMM ''yy";
    case 'datetime':
      return 'dd / MMM / yyyy • HH:mm';
    case 'datetime-short':
      return 'dd / MMM • HH:mm';
    case 'datetime-seconds':
      return 'dd / MMM / yyyy HH:mm:ss';
    case 'weekday-datetime':
      return "E, d MMM 'at' HH:mm";
    case 'date-countdown':
      return 'dd / MMM / yyyy • ';
  }
}

export function abbreviateDate(
  date: string | Date | null | undefined,
  lang: 'is' | 'en',
  display: DateDisplay = 'date'
): string {
  if (!date) return '';
  if (typeof date !== 'string' && !(date instanceof Date)) return String(date);
  const dateFormat = getDateDisplayFormat(display);
  const dateObject = parseDate(date);
  let formattedDate = '';
  try {
    formattedDate = format(dateObject, dateFormat, { locale: dateFnsLocales[lang] });
  } catch (e) {
    return String(date);
  }
  if (display === 'date-countdown') {
    return formattedDate + formatDistanceToNow(dateObject, { addSuffix: true });
  }
  return formattedDate;
}

export function getRelativeDate(
  dateStr: string,
  comparator: 'lt' | 'gt' | '',
  days: number
): string {
  const date = new Date(dateStr);

  if (isNaN(date.getTime())) {
    throw new Error('Invalid date string');
  }

  if (comparator === 'lt') {
    date.setDate(date.getDate() - days);
  } else if (comparator === 'gt') {
    date.setDate(date.getDate() + days);
  }

  return date.toISOString();
}

export function addTimeToDate(dateStr: string, timeStr: string) {
  const datePart = dateStr.split('T')[0];
  const combinedDateTime = `${datePart}T${timeStr}:00+00:00`;
  return combinedDateTime;
}

/**
 * Returns the number of days until the given future date.
 *
 * @param futureDateString - A date string representing a future date.
 * @returns The number of full days until the date arrives.
 * @throws Error if the provided date string is invalid.
 */
export function daysUntil(futureDateString: Date | string): number {
  const now = new Date();
  const futureDate = new Date(futureDateString);

  if (isNaN(futureDate.getTime())) {
    throw new Error('Invalid date string provided.');
  }

  const diffMilliseconds = futureDate.getTime() - now.getTime();

  if (diffMilliseconds <= 0) {
    return 0;
  }

  const days = diffMilliseconds / day;

  return Math.ceil(days);
}
